/*
  The full-screen shortlist (snippets/shortlist-modal.liquid, a <tat-drawer> on every page). Draws the saved list
  from assets/shortlist.js (window.TAT.shortlist, so there is one copy of the list in memory), when it opens:
  - Cards draw instantly from the saved snapshots, then pick up the latest price, stock line and image from
    /products/HANDLE?section_id=shortlist-item (the same rules as product cards).
  - Notes on each product and a general note save on this device as they type (no redraw while typing).
  - Removing a card animates it away and offers Undo. Recently viewed products save with one tap.
  - Get a quote is the default next step (most people shortlisting aren't near the showroom); Book a listen gets the
    list in "Would like to hear". Both forms carry the list and notes through
    assets/shortlist.js; assets/theme.js validates them, and after posting reopens this overlay on the thank-you.
  - Phones: a bottom bar keeps Get a quote and Book a listen one tap away until the panel is on screen.
*/
const root = document.getElementById('ShortlistModal');

const ready = () =>
  new Promise((resolve) => {
    if (window.TAT?.shortlist) resolve(window.TAT.shortlist);
    else document.addEventListener('shortlist:ready', () => resolve(window.TAT.shortlist), { once: true });
  });

if (root) {
  const sl = await ready();
  const $ = (selector, el = root) => el.querySelector(selector);
  const list = $('[data-sl-list]');
  const itemTemplate = $('template[data-sl-item]');
  const recentWrap = $('[data-sl-recent]');
  const recentList = $('[data-sl-recent-list]');
  const recentTemplate = $('template[data-sl-recent-item]');
  const generalNote = $('[data-sl-general-note]');
  const panel = $('[data-sl-panel]');
  const mobileBar = $('[data-sl-mobilebar]');
  const live = new Map();
  const scroller = $('[data-sl-scroll]');
  const posted = !!root.querySelector('[data-page-enquiry-success]:not([hidden])');
  const isOpen = () => root.classList.contains('is-open');
  let stale = true;

  const autosize = (el) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  };
  const flash = (el) => {
    if (!el) return;
    el.setAttribute('data-show', '');
    clearTimeout(el.timer);
    el.timer = setTimeout(() => el.removeAttribute('data-show'), 1400);
  };
  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };
  const fill = (el, item) => {
    el.dataset.handle = item.handle;
    el.querySelectorAll('[data-item-link]').forEach((a) => (a.href = item.url));
    const img = el.querySelector('[data-item-image]');
    const src = live.get(item.handle)?.image || item.image;
    if (src) img.src = src;
    else img.removeAttribute('src');
    el.querySelector('[data-item-vendor]').textContent = item.vendor;
    el.querySelector('[data-item-title]').textContent = item.title;
  };

  // ------------------------------------------------------------------------------------------------------------
  // Cards
  // ------------------------------------------------------------------------------------------------------------
  function paintLive(li) {
    const data = live.get(li.dataset.handle);
    const item = sl.items.find((i) => i.handle === li.dataset.handle);
    const price = li.querySelector('[data-item-price]');
    const stock = li.querySelector('[data-item-stock]');
    if (data?.gone) {
      price.textContent = '';
      stock.innerHTML = '<span class="text-xs text-muted">No longer on the website. Ask us about it.</span>';
    } else if (data) {
      price.innerHTML = data.price;
      stock.innerHTML = data.stock;
      if (data.image) li.querySelector('[data-item-image]').src = data.image;
    } else {
      price.textContent = item?.price || '';
    }
  }

  function makeCard(item) {
    const li = itemTemplate.content.firstElementChild.cloneNode(true);
    fill(li, item);
    li.querySelector('[data-sl-remove]').setAttribute('aria-label', `Remove ${item.title} from your shortlist`);
    const note = li.querySelector('[data-sl-note]');
    note.value = item.note || '';
    note.setAttribute('aria-label', `Your note on ${item.title}`);
    if (item.note) {
      li.querySelector('[data-sl-note-wrap]').hidden = false;
      li.querySelector('[data-sl-note-open]').hidden = true;
    }
    paintLive(li);
    // Arrive softly.
    li.classList.add('opacity-0', 'translate-y-2');
    requestAnimationFrame(() => requestAnimationFrame(() => li.classList.remove('opacity-0', 'translate-y-2')));
    return li;
  }

  async function fetchLive(items) {
    const todo = items.filter((i) => !live.has(i.handle));
    await Promise.all(
      todo.map(async (item) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(item.handle)}?section_id=shortlist-item`);
          if (res.status === 404) {
            live.set(item.handle, { gone: true });
            return;
          }
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const el = doc.querySelector('[data-shortlist-live]');
          if (!el) return;
          live.set(item.handle, {
            price: el.querySelector('[data-live-price]')?.innerHTML || '',
            stock: el.querySelector('[data-live-stock]')?.innerHTML || '',
            image: el.dataset.image ? (el.dataset.image.startsWith('//') ? `https:${el.dataset.image}` : el.dataset.image) : '',
          });
        } catch {
          // Offline or blocked: the snapshot price stays.
        }
      })
    );
    list.querySelectorAll('[data-sl-card]').forEach(paintLive);
  }

  function drawRecent() {
    const recent = sl.recent().slice(0, 10);
    recentWrap.hidden = recent.length === 0;
    recentList.replaceChildren(
      ...recent.map((item) => {
        const li = recentTemplate.content.firstElementChild.cloneNode(true);
        fill(li, item);
        const add = li.querySelector('[data-sl-add]');
        add.dataset.handle = item.handle;
        add.setAttribute('aria-label', `Save ${item.title} to your shortlist`);
        return li;
      })
    );
  }

  function draw() {
    const items = sl.items;
    const existing = new Map([...list.querySelectorAll('[data-sl-card]')].map((li) => [li.dataset.handle, li]));
    list.replaceChildren(...items.map((item) => existing.get(item.handle) || makeCard(item)));
    const has = items.length > 0;
    $('[data-sl-empty]').hidden = has;
    $('[data-sl-general]').hidden = !has;
    root.querySelectorAll('[data-sl-toolbar], [data-sl-availability]').forEach((el) => (el.hidden = !has));
    panel.hidden = !has && !posted;
    const hear = $('[data-sl-hear]');
    if (hear) hear.value = items.map((i) => i.title).join(', ');
    drawRecent();
    updateMobileBar();
    fetchLive(items);
  }

  // ------------------------------------------------------------------------------------------------------------
  // Mobile bar: shown on phones while the next-step panel is off screen
  // ------------------------------------------------------------------------------------------------------------
  let panelVisible = false;
  function updateMobileBar() {
    if (!mobileBar) return;
    mobileBar.hidden = posted || sl.items.length === 0 || panelVisible;
  }
  new IntersectionObserver(
    ([entry]) => {
      panelVisible = entry.isIntersecting;
      updateMobileBar();
    },
    { root: scroller }
  ).observe(panel);

  // After a form posts, the panel shows only its thank-you.
  if (posted) {
    $('[data-sl-tabs]').hidden = true;
    root.querySelectorAll('[data-sl-pane]').forEach((pane) => (pane.hidden = true));
  }

  // ------------------------------------------------------------------------------------------------------------
  // Notes
  // ------------------------------------------------------------------------------------------------------------
  const saveNote = debounce((handle, value, indicator) => {
    sl.save(
      sl.items.map((i) => (i.handle === handle ? { ...i, note: value.trim() } : i)),
      { silent: true }
    );
    flash(indicator);
  }, 400);
  const saveGeneral = debounce((value) => {
    sl.setGeneralNote(value.trim());
    flash($('[data-sl-general-saved]'));
  }, 400);

  generalNote.value = sl.generalNote();
  root.addEventListener('input', (e) => {
    const note = e.target.closest('[data-sl-note]');
    if (note) {
      autosize(note);
      const card = note.closest('[data-sl-card]');
      saveNote(card.dataset.handle, note.value, card.querySelector('[data-sl-saved]'));
      return;
    }
    if (e.target === generalNote) {
      autosize(generalNote);
      saveGeneral(generalNote.value);
    }
  });

  // ------------------------------------------------------------------------------------------------------------
  // Clicks
  // ------------------------------------------------------------------------------------------------------------
  const undo = $('[data-sl-undo]');
  let undoTimer;
  let removed = null;
  function offerUndo(item, index) {
    removed = { item, index };
    undo.querySelector('[data-sl-undo-text]').textContent = `Removed ${item.title}`;
    undo.hidden = false;
    requestAnimationFrame(() => undo.setAttribute('data-show', ''));
    clearTimeout(undoTimer);
    undoTimer = setTimeout(() => {
      undo.removeAttribute('data-show');
      setTimeout(() => (undo.hidden = true), 300);
      removed = null;
    }, 5000);
  }

  root.addEventListener('click', (e) => {
    const open = e.target.closest('[data-sl-note-open]');
    if (open) {
      const wrap = open.parentElement.querySelector('[data-sl-note-wrap]');
      open.hidden = true;
      wrap.hidden = false;
      const note = wrap.querySelector('[data-sl-note]');
      autosize(note);
      note.focus();
      return;
    }

    const remove = e.target.closest('[data-sl-remove]');
    if (remove) {
      const card = remove.closest('[data-sl-card]');
      const index = sl.items.findIndex((i) => i.handle === card.dataset.handle);
      const item = sl.items[index];
      card.classList.add('opacity-0', 'scale-[0.98]');
      setTimeout(() => {
        sl.save(sl.items.filter((i) => i.handle !== item.handle));
        sl.track('remove_from_wishlist', { ecommerce: { items: sl.gaItems([item]) } });
        offerUndo(item, index);
        (list.querySelector('[data-sl-remove]') || $('h1'))?.focus({ preventScroll: true });
      }, 220);
      return;
    }

    if (e.target.closest('[data-sl-undo-button]') && removed) {
      const next = [...sl.items];
      next.splice(Math.min(removed.index, next.length), 0, removed.item);
      sl.save(next);
      sl.track('add_to_wishlist', { ecommerce: { items: sl.gaItems([removed.item]) } });
      removed = null;
      undo.removeAttribute('data-show');
      setTimeout(() => (undo.hidden = true), 300);
      return;
    }

    const add = e.target.closest('[data-sl-add]');
    if (add) {
      const item = sl.recent().find((r) => r.handle === add.dataset.handle);
      if (!item) return;
      sl.save([{ ...item, added: Date.now() }, ...sl.items]);
      sl.track('add_to_wishlist', { ecommerce: { items: sl.gaItems([item]) } });
      return;
    }

    const prompt = e.target.closest('[data-sl-prompt]');
    if (prompt) {
      const label = `${prompt.dataset.slPrompt}: `;
      const current = generalNote.value.replace(/\s+$/, '');
      generalNote.value = current ? `${current}\n${label}` : label;
      autosize(generalNote);
      generalNote.focus();
      generalNote.setSelectionRange(generalNote.value.length, generalNote.value.length);
      saveGeneral(generalNote.value);
      return;
    }

    const jump = e.target.closest('[data-sl-jump]');
    if (jump) {
      e.preventDefault();
      const tab = document.getElementById(`ShortlistTab-${jump.dataset.slJump}`);
      if (tab) {
        tab.checked = true;
        showPane(tab.value);
      }
      panel.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  });

  // Get a quote / Book a listen: the switch sets data-sl-next on the panel (CSS shows the matching form).
  const showPane = (value) => {
    const group = root.querySelector('[data-sl-next]');
    if (group) group.dataset.slNext = value;
  };
  root.addEventListener('change', (e) => {
    if (e.target.matches('input[name="sl-next"]')) showPane(e.target.value);
  });

  // Count the leads these forms create (theme.js stops invalid ones before this runs).
  root.addEventListener('submit', (e) => {
    const kind = e.target.dataset.shortlistForm;
    if (!kind) return;
    sl.track(kind === 'book' ? 'shortlist_book' : 'shortlist_enquire', {
      shortlist_count: sl.items.length,
      ecommerce: { items: sl.gaItems(sl.items) },
    });
  });

  // Draw when it opens (and live while open); saves made while it's closed just mark it for a redraw.
  const show = () => {
    if (stale) draw();
    stale = false;
    generalNote.value = sl.generalNote();
    requestAnimationFrame(() => root.querySelectorAll('.sl-note').forEach((t) => !t.closest('[hidden]') && autosize(t)));
  };
  root.addEventListener('drawer:open', show);
  document.addEventListener('shortlist:change', () => {
    if (isOpen()) draw();
    else stale = true;
  });
  if (isOpen()) show();
}
